import { useState, useEffect, useRef } from "react";
import {
  PanelLeft,
  Search,
  Moon,
  Sun,
  RotateCw,
  Download,
  Maximize,
  Volume2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Minus,
  Plus,
  X,
} from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import type { FitMode } from "../PdfViewer";

interface PdfToolbarProps {
  currentPage: number;
  numPages: number;
  onPageChange: (page: number) => void;

  zoom: number;
  onZoomChange: (zoom: number) => void;

  fitMode: FitMode;
  onFitModeChange: (mode: FitMode) => void;

  onToggleThumbnails: () => void;
  thumbnailsOpen: boolean;

  onRotate: () => void;
  rotation: number;

  onDownload: () => void;

  darkMode: boolean;
  onToggleTheme: () => void;

  onToggleFullscreen: () => void;

  onReadAloudClick?: () => void;

  // Search props
  isSearchOpen: boolean;
  onToggleSearch: () => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  searchResultsCount: number;
  activeMatchIndex: number;
  onNextMatch: () => void;
  onPrevMatch: () => void;
  searchInputRef?: React.RefObject<HTMLInputElement>;

  // Container ref for dropdown portal (needed for fullscreen)
  containerRef?: React.RefObject<HTMLElement>;
}

const ZOOM_PRESETS = [50, 75, 100, 125, 150, 200, 300];

const clamp = (v: number, min: number, max: number) =>
  Math.min(max, Math.max(min, v));

export function PdfToolbar({
  currentPage,
  numPages,
  onPageChange,

  zoom,
  onZoomChange,

  fitMode,
  onFitModeChange,

  thumbnailsOpen,
  onToggleThumbnails,

  onRotate,

  onDownload,

  darkMode,
  onToggleTheme,

  onToggleFullscreen,

  onReadAloudClick,

  // Search props
  isSearchOpen,
  onToggleSearch,
  searchQuery,
  onSearchChange,
  searchResultsCount,
  activeMatchIndex,
  onNextMatch,
  onPrevMatch,
  searchInputRef,
  containerRef,
}: PdfToolbarProps) {
  const [pageInput, setPageInput] = useState(String(currentPage));
  const internalSearchRef = useRef<HTMLInputElement>(null);
  const effectiveSearchRef = searchInputRef ?? internalSearchRef;

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  const handlePageSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = Number(pageInput);
    if (Number.isNaN(parsed)) {
      setPageInput(String(currentPage));
      return;
    }
    const clamped = Math.max(1, Math.min(parsed, numPages));
    onPageChange(clamped);
  };

  const zoomPercent = Math.round(zoom * 100);

  const fitLabel =
    fitMode === "page-width"
      ? "Page width"
      : fitMode === "page-fit"
      ? "Page fit"
      : fitMode === "actual"
      ? "Actual size"
      : `${zoomPercent}%`;

  const setZoomCustom = (percent: number) => {
    onFitModeChange("custom");
    onZoomChange(clamp(percent / 100, 0.4, 4));
  };

  const handleZoomDelta = (delta: number) => {
    onFitModeChange("custom");
    onZoomChange(clamp(zoom + delta, 0.4, 4));
  };

  return (
    <div className="flex items-center gap-2 border-b border-border bg-background px-2 py-1.5 text-sm text-muted-foreground">
      {/* Thumbnail toggle */}
      <button
        type="button"
        className={`rounded-full px-2 py-1 transition-colors hover:bg-muted hover:text-foreground ${
          thumbnailsOpen ? "bg-muted text-foreground" : ""
        }`}
        title="Toggle Thumbnails"
        onClick={onToggleThumbnails}
      >
        <PanelLeft className="h-4 w-4" />
      </button>

      {/* Search bar */}
      {isSearchOpen ? (
        <div className="ml-[-8px] flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            ref={effectiveSearchRef}
            className="w-32 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
            placeholder="Find in document..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (e.shiftKey) {
                  onPrevMatch();
                } else {
                  onNextMatch();
                }
              } else if (e.key === "Escape") {
                onToggleSearch();
              }
            }}
            autoFocus
          />
          <span className="min-w-[40px] text-center text-xs tabular-nums text-muted-foreground">
            {searchResultsCount > 0
              ? `${activeMatchIndex + 1}/${searchResultsCount}`
              : searchQuery
              ? "0"
              : ""}
          </span>
          <button
            type="button"
            className="rounded p-0.5 hover:bg-muted disabled:opacity-40"
            onClick={onPrevMatch}
            disabled={searchResultsCount === 0}
            title="Previous match (Shift+Enter)"
          >
            <ChevronUp className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            className="rounded p-0.5 hover:bg-muted disabled:opacity-40"
            onClick={onNextMatch}
            disabled={searchResultsCount === 0}
            title="Next match (Enter)"
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            className="rounded p-0.5 hover:bg-muted"
            onClick={onToggleSearch}
            title="Close search (Esc)"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="ml-[-8px] rounded-full px-2 py-1 transition-colors hover:bg-muted hover:text-foreground"
          title="Search (Ctrl+F)"
          onClick={onToggleSearch}
        >
          <Search className="h-4 w-4" />
        </button>
      )}

      {/* Theme toggle */}
      <button
        type="button"
        className="ml-[-8px] rounded-full px-2 py-1 transition-colors hover:bg-muted hover:text-foreground"
        title="Switch PDF theme"
        onClick={onToggleTheme}
      >
        {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </button>

      {/* Read aloud (UI-only for now) */}
      <button
        type="button"
        className="ml-[-8px] rounded-full px-2 py-1 transition-colors hover:bg-muted hover:text-foreground"
        title="Read aloud"
        onClick={onReadAloudClick}
      >
        <Volume2 className="h-4 w-4" />
      </button>

      <span className="flex-grow" />

      {/* Page navigation */}
      <button
        type="button"
        className="rounded-full p-1 hover:bg-muted hover:text-foreground disabled:opacity-40"
        onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
        disabled={currentPage <= 1}
        title="Previous page"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>

      {/* Page input */}
      <form
        onSubmit={handlePageSubmit}
        className="flex items-center gap-1"
        title="Go to page"
      >
        <input
          className="w-10 rounded-md border border-border bg-background px-1 py-0.5 text-center text-sm text-foreground"
          type="text"
          value={pageInput}
          onChange={(e) => setPageInput(e.target.value)}
        />
        <span className="text-xs text-muted-foreground tabular-nums">
          / {numPages}
        </span>
      </form>

      <button
        type="button"
        className="rounded-full p-1 hover:bg-muted hover:text-foreground disabled:opacity-40"
        onClick={() => onPageChange(Math.min(currentPage + 1, numPages))}
        disabled={currentPage >= numPages}
        title="Next page"
      >
        <ChevronRight className="h-4 w-4" />
      </button>

      {/* Divider */}
      <div className="mx-1 h-5 w-px bg-border" />

      {/* Page fit / zoom dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground hover:bg-muted"
          >
            <span>{fitLabel}</span>
            <ChevronDown className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[10rem] bg-popover" container={containerRef?.current}>
          {/* Zoom controls row */}
          <DropdownMenuItem
            className="cursor-default !p-0 focus:bg-transparent"
            onSelect={(e) => e.preventDefault()}
          >
            <div className="flex w-full items-center px-2 py-1">
              <span className="mr-auto text-sm">{zoomPercent}%</span>
              <button
                type="button"
                className="mx-0.5 rounded-sm border border-border p-0.5 hover:bg-accent"
                aria-label="Zoom out"
                onClick={(e) => {
                  e.stopPropagation();
                  handleZoomDelta(-0.1);
                }}
              >
                <Minus className="h-4 w-4" />
              </button>
              <button
                type="button"
                className="mx-0.5 rounded-sm border border-border p-0.5 hover:bg-accent"
                aria-label="Zoom in"
                onClick={(e) => {
                  e.stopPropagation();
                  handleZoomDelta(0.1);
                }}
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {/* Fit modes */}
          <DropdownMenuItem
            className={`cursor-pointer ${fitMode === "page-width" ? "bg-accent" : ""}`}
            onSelect={() => onFitModeChange("page-width")}
          >
            Page width
          </DropdownMenuItem>
          <DropdownMenuItem
            className={`cursor-pointer ${fitMode === "page-fit" ? "bg-accent" : ""}`}
            onSelect={() => onFitModeChange("page-fit")}
          >
            Page fit
          </DropdownMenuItem>
          <DropdownMenuItem
            className={`cursor-pointer ${fitMode === "actual" ? "bg-accent" : ""}`}
            onSelect={() => onFitModeChange("actual")}
          >
            Actual size (100%)
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {/* Percentage presets */}
          {ZOOM_PRESETS.map((p) => (
            <DropdownMenuItem
              key={p}
              className={`cursor-pointer ${zoomPercent === p && fitMode === "custom" ? "bg-accent" : ""}`}
              onSelect={() => setZoomCustom(p)}
            >
              {p}%
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <span className="flex-grow" />

      {/* Right cluster: rotate / download / fullscreen */}
      <div className="flex items-center gap-1">
        <button
          type="button"
          className="rounded-full p-1.5 transition-colors hover:bg-muted hover:text-foreground"
          title="Rotate 90° Clockwise"
          onClick={onRotate}
        >
          <RotateCw className="h-4 w-4" />
        </button>

        <button
          type="button"
          className="rounded-full p-1.5 transition-colors hover:bg-muted hover:text-foreground"
          title="Download PDF"
          onClick={onDownload}
        >
          <Download className="h-4 w-4" />
        </button>

        <button
          type="button"
          className="rounded-full p-1.5 transition-colors hover:bg-muted hover:text-foreground"
          title="Full Screen"
          onClick={onToggleFullscreen}
        >
          <Maximize className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
